/**
 * Firebase Storage Upload Service
 * Handelt het uploaden van foto's naar Firebase Storage
 */

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';

export class FirebaseStorageUploadService {
  /**
   * Upload een foto naar Firebase Storage
   * @param {File} file - Het bestand om te uploaden
   * @param {string} folder - Folder pad (bijv. 'profiles', 'properties/{propertyId}')
   * @param {string} fileName - Optionele custom bestandsnaam
   * @returns {Promise<{url: string, path: string}>}
   */
  static async uploadPhoto(file, folder = 'uploads', fileName = null) {
    if (!file) {
      throw new Error('Geen bestand opgegeven');
    }

    // Valideer bestandstype
    if (!file.type.startsWith('image/')) {
      throw new Error('Alleen afbeeldingen zijn toegestaan');
    }

    // Valideer bestandsgrootte (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('Bestand is te groot. Maximum 10MB toegestaan.');
    }

    try {
      // Genereer bestandsnaam
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const finalFileName = fileName || `photo_${timestamp}.${fileExtension}`;
      
      // Maak storage referentie
      const storagePath = `${folder}/${finalFileName}`;
      const storageRef = ref(storage, storagePath);
      
      console.log('Uploading to Firebase Storage:', {
        path: storagePath,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Upload bestand
      const snapshot = await uploadBytes(storageRef, file);
      
      // Haal download URL op
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Firebase Storage upload successful:', {
        path: storagePath,
        url: downloadURL
      });

      return {
        url: downloadURL,
        path: storagePath
      };
    } catch (error) {
      console.error('Error uploading to Firebase Storage:', error);
      
      // Provide helpful error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('Geen toestemming om bestanden te uploaden. Controleer Firebase Storage rules.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload geannuleerd.');
      } else if (error.code === 'storage/unknown') {
        throw new Error('Onbekende fout bij uploaden. Probeer het opnieuw.');
      }
      
      throw new Error(`Upload mislukt: ${error.message}`);
    }
  }

  /**
   * Upload meerdere foto's tegelijk naar Firebase Storage
   * @param {File[]} files - Array van bestanden
   * @param {string} folder - Folder pad
   * @returns {Promise<Array<{url: string, path: string}>>}
   */
  static async uploadMultiplePhotos(files, folder = 'uploads') {
    if (!files || files.length === 0) {
      throw new Error('Geen bestanden opgegeven');
    }

    console.log(`Uploading ${files.length} photos to Firebase Storage folder: ${folder}`);
    
    const uploadPromises = files.map((file, index) => {
      const timestamp = Date.now();
      const fileName = `photo_${timestamp}_${index}.${file.name.split('.').pop() || 'jpg'}`;
      return this.uploadPhoto(file, folder, fileName);
    });

    try {
      const results = await Promise.all(uploadPromises);
      console.log('All photos uploaded successfully to Firebase Storage');
      return results;
    } catch (error) {
      console.error('Error uploading multiple photos to Firebase Storage:', error);
      throw new Error(`Upload van meerdere foto's mislukt: ${error.message}`);
    }
  }

  /**
   * Verwijder een foto uit Firebase Storage
   * @param {string} path - Storage pad van het bestand
   * @returns {Promise<void>}
   */
  static async deletePhoto(path) {
    if (!path) {
      throw new Error('Geen pad opgegeven');
    }

    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      console.log('Photo deleted from Firebase Storage:', path);
    } catch (error) {
      console.error('Error deleting photo from Firebase Storage:', error);
      
      // Don't throw if file doesn't exist
      if (error.code !== 'storage/object-not-found') {
        throw new Error(`Verwijderen mislukt: ${error.message}`);
      }
    }
  }

  /**
   * Verwijder meerdere foto's uit Firebase Storage
   * @param {string[]} paths - Array van storage paden
   * @returns {Promise<void>}
   */
  static async deleteMultiplePhotos(paths) {
    if (!paths || paths.length === 0) {
      return;
    }

    console.log(`Deleting ${paths.length} photos from Firebase Storage`);
    
    const deletePromises = paths.map(path => this.deletePhoto(path));
    
    try {
      await Promise.all(deletePromises);
      console.log('All photos deleted successfully from Firebase Storage');
    } catch (error) {
      console.error('Error deleting multiple photos from Firebase Storage:', error);
      throw new Error(`Verwijderen van meerdere foto's mislukt: ${error.message}`);
    }
  }

  /**
   * Upload a document (PDF) to Firebase Storage
   * @param {File} file - The file to upload
   * @param {string} folder - Folder path (e.g. 'motivations/{propertyId}')
   * @param {string} fileName - Optional custom file name
   * @returns {Promise<{url: string, path: string}>}
   */
  static async uploadDocument(file, folder = 'documents', fileName = null) {
    if (!file) {
      throw new Error('Geen bestand opgegeven');
    }

    if (file.type !== 'application/pdf') {
      throw new Error('Alleen PDF bestanden zijn toegestaan');
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('Bestand is te groot. Maximum 10MB toegestaan.');
    }

    try {
      const timestamp = Date.now();
      const finalFileName = fileName || `document_${timestamp}.pdf`;
      const storagePath = `${folder}/${finalFileName}`;
      const storageRef = ref(storage, storagePath);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        path: storagePath
      };
    } catch (error) {
      console.error('Error uploading document to Firebase Storage:', error);

      if (error.code === 'storage/unauthorized') {
        throw new Error('Geen toestemming om bestanden te uploaden. Controleer Firebase Storage rules.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload geannuleerd.');
      } else if (error.code === 'storage/unknown') {
        throw new Error('Onbekende fout bij uploaden. Probeer het opnieuw.');
      }

      throw new Error(`Upload mislukt: ${error.message}`);
    }
  }
}

